import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { ProjectManagementTask, ProjectManagementMilestone } from '/imports/api/projectManagement';

const DAY_MS = 86400000;
const CELLS = 42;  // 6 rows × 7 columns, fixed so the pane height never jumps between months
const TASKS_PER_CELL = 3;
const MILESTONES_PER_CELL = 2;
const PAST_MONTHS = 3;  // how much history the at-a-glance strip keeps before the current month

const ScheduleStyled = styled.div`
  /* At-a-glance month strip: one chip per month that HAS activity, so empty stretches collapse rather
     than pad the row. Single line, clipped — chips that would be cut are hidden by the fit measurement
     below, never rendered half-off the edge. */
  .glance { display: flex; flex-wrap: nowrap; gap: 0.35rem; overflow: hidden; margin-bottom: 0.6rem; }
  .glance .chip {
    flex: none; border: 1px solid ${css.grey7}; border-radius: 0.35rem; background: ${css.grey9};
    color: ${css.grey2}; font: inherit; font-size: 0.75rem; font-weight: 600; white-space: nowrap;
    padding: 0.25rem 0.55rem; cursor: pointer;
  }
  .glance .chip:hover { border-color: ${css.primaryColor}; color: ${css.grey1}; }
  .glance .chip.selected { background: ${css.primaryColor}; border-color: ${css.primaryColor}; color: ${css.white}; }
  /* The month containing today reads as an anchor even when another month is selected. */
  .glance .chip.current { border-color: ${css.grey4}; }
  .glance .chip .count { font-weight: normal; opacity: 0.75; }
  .cal-head { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.6rem; }
  .cal-head .month { font-weight: 600; font-size: 1rem; min-width: 11rem; text-align: center; }
  .cal-head .nav { background: none; border: none; padding: 0.1rem; cursor: pointer; display: inline-flex; color: ${css.grey2}; }
  .cal-head .nav:hover { color: ${css.primaryColor}; }
  .cal-head .nav svg { width: 1.4rem; height: 1.4rem; }
  .cal-head .spacer { flex: 1; }
  .dow { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; }
  .dow > span {
    text-align: center; padding-bottom: 0.2rem;
    color: ${css.grey4}; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
  }
  .grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; }
  .cell {
    min-width: 0; min-height: 4.6rem; box-sizing: border-box;
    border: 1px solid ${css.grey7}; border-radius: 0.3rem; background: ${css.white};
    padding: 0.2rem 0.3rem; display: flex; flex-direction: column; gap: 0.1rem; overflow: hidden;
  }
  .cell.out { background: ${css.grey9}; }
  .cell.out .num { color: ${css.grey5}; }
  .cell.ms { background: ${css.milestoneBlue}; border-color: ${css.primaryColor}; }
  .cell.today { box-shadow: inset 0 0 0 2px ${css.primaryColor}; }
  .cell .num { font-size: 0.72rem; font-weight: 600; color: ${css.grey3}; line-height: 1.2; }
  /* One row per item, milestones then tasks: a milestone leads with its health dot and a bold label, a
     task with a checkmark and a plain one — so the two are told apart by their mark AND their weight. */
  .cell .item {
    display: flex; align-items: center; gap: 0.25rem; min-width: 0; width: 100%;
    font-size: 0.68rem; line-height: 1.25; text-align: left;
    border: none; background: none; padding: 0; margin: 0; font-family: inherit;
  }
  /* The text needs its own box: as a bare child of a flex row it is an anonymous item, which
     text-overflow cannot reach, and the label clips mid-word with no ellipsis. */
  .cell .item > .txt { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cell .item.ms { font-weight: 600; color: ${css.grey1}; }
  .cell .item.task { color: ${css.grey2}; cursor: pointer; border-radius: 0.2rem; }
  .cell .item.task > .txt { font-weight: 500; }
  .cell .item.task.static { cursor: default; }
  .cell .item.task:not(.static):hover { background: ${css.lightBlue}; color: ${css.grey1}; }
  .cell .item .ms-dot { width: 0.45rem; height: 0.45rem; border-radius: 50%; flex: none; margin: 0 0.05rem; }
  .cell .item .icon { flex: none; }
  .cell .item svg { display: block; width: 0.7rem; height: 0.7rem; min-width: 0; }
  .cell .more { font-size: 0.62rem; color: ${css.grey4}; line-height: 1; }
  .cal-foot { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; margin: 0.6rem 0 0.2rem; font-size: 0.75rem; color: ${css.grey3}; }
  .cal-foot .key { display: inline-flex; align-items: center; gap: 0.3rem; }
  .cal-foot .key svg { width: 0.8rem; height: 0.8rem; min-width: 0; }
  .cal-foot .key .swatch { width: 0.7rem; height: 0.7rem; border-radius: 0.2rem; border: 1px solid ${css.primaryColor}; background: ${css.milestoneBlue}; }
  .cal-foot .undated { margin-left: auto; color: ${css.grey4}; }
  .empty { padding: 2rem 0; color: ${css.grey4}; }
`;

/** Calendar day of a stored date, as YYYY-MM-DD in UTC. */
const dayKey = (d: Date): string => new Date(d).toISOString().slice(0, 10);

interface DayItems {
  estimated: ProjectManagementTask[];
  actual: ProjectManagementTask[];
  planned: ProjectManagementMilestone[];
  done: ProjectManagementMilestone[];
}

const emptyDay = (): DayItems => ({ estimated: [], actual: [], planned: [], done: [] });

/**
 * The 42 days of a displayed month, week starting Sunday (the codebase's only convention).
 * @param monthStart - ms of the UTC first-of-month being displayed
 * @returns one entry per cell, in render order
 */
const monthCells = (monthStart: number): { key: string; num: number; inMonth: boolean }[] => {
  const month = new Date(monthStart).getUTCMonth();
  const start = monthStart - new Date(monthStart).getUTCDay() * DAY_MS;
  return Array.from({ length: CELLS }, (_, i) => {
    const d = new Date(start + i * DAY_MS);
    return { key: dayKey(d), num: d.getUTCDate(), inMonth: d.getUTCMonth() === month };
  });
};

/**
 * Shift a UTC first-of-month by whole months.
 * @param monthStart - ms of a UTC first-of-month
 * @param months - offset, may be negative
 * @returns ms of the shifted UTC first-of-month
 */
const shiftMonth = (monthStart: number, months: number): number => {
  const d = new Date(monthStart);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1);
};

/**
 * Month label for a header, e.g. "August 2026".
 * @param monthStart - ms of a UTC first-of-month
 * @param short - use the 3-letter month name
 * @returns localized label
 */
const monthLabel = (monthStart: number, short?: boolean): string => {
  const d = new Date(monthStart);
  const m = d.getUTCMonth();
  return `${short ? strings.MonthOfYearShort(m) : strings.MonthOfYear(m)} ${d.getUTCFullYear()}`;
};

interface Props {
  tasks: ProjectManagementTask[];
  /** The milestone layer, rendered read-only (no edit/delete here). Members get it as schedule context over their own tasks. */
  milestones: ProjectManagementMilestone[];
  /** Milestone dot color, so the calendar agrees with the timeline rail beside it. */
  milestoneColor?: (m: ProjectManagementMilestone) => string;
  onOpenTask?: (taskId: string) => void;
  /** Resolve a project name, to prefix each entry with it — used by the pipeline calendar, which spans
   *  many projects. Omitted on the single-project detail page, where the label alone is unambiguous. */
  projectName?: (projectId: string) => string | undefined;
}

/**
 * Schedule tab: one project's tasks and milestones laid out on a month calendar, under a compact
 * at-a-glance strip of the months that actually carry something (see glanceMonths).
 *
 * Days are bucketed by UTC day on purpose. The three date sources are stored under three different
 * conventions — milestone plannedDate as a seeded wall-clock instant, a hand-typed milestone actual as
 * UTC midnight, task dates as local midnight — so no single rule reads all three back correctly
 * everywhere. UTC is the one that lands every source on the day a US-timezone user entered it; the
 * durable fix is normalizing storage, which is separate work.
 */
export const ProjectManagementScheduleView = ({ tasks, milestones, milestoneColor, onOpenTask, projectName }: Props) => {
  /** An entry's display text, prefixed with its project name when a resolver is supplied. */
  const withProject = (label: string, projectId: string) => {
    const name = projectName?.(projectId);
    return name ? `${name} · ${label}` : label;
  };
  const [monthStart, setMonthStart] = useState<number>(() => {
    const n = new Date();
    return Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), 1);
  });

  const { byDay, undated } = useMemo(() => {
    const index = new Map<string, DayItems>();
    const at = (d: Date): DayItems => {
      const key = dayKey(d);
      const existing = index.get(key);
      if (existing) return existing;
      const fresh = emptyDay();
      index.set(key, fresh);
      return fresh;
    };
    for (const t of tasks) {
      if (t.estimatedDate) at(t.estimatedDate).estimated.push(t);
      if (t.actualDate) at(t.actualDate).actual.push(t);
    }
    // A completed milestone keeps its planned day AND gains one for its actual — moving it would erase
    // the slip, which is the one thing a schedule view exists to show.
    for (const m of milestones) {
      if (m.plannedDate) at(m.plannedDate).planned.push(m);
      if (m.actualDate) at(m.actualDate).done.push(m);
    }
    return { byDay: index, undated: tasks.filter((t) => !t.estimatedDate && !t.actualDate).length };
  }, [tasks, milestones]);

  const todayKey = dayKey(new Date());
  const cells = monthCells(monthStart);
  const thisMonth = Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1);

  // Months carrying anything at all, each with its item count. Months with nothing are skipped
  // entirely — a project that pauses for a quarter should not spend three chips saying so.
  const glanceMonths = useMemo(() => {
    const counts = new Map<number, number>();
    for (const [key, day] of byDay) {
      const d = new Date(`${key}T00:00:00Z`);
      const month = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
      const n = day.estimated.length + day.actual.length + day.planned.length + day.done.length;
      counts.set(month, (counts.get(month) || 0) + n);
    }
    const all = [...counts.entries()].sort((a, b) => a[0] - b[0]);
    // Keep the last PAST_MONTHS months of history; everything from this month on is what is still to
    // come, and the fit measurement decides how much of it survives.
    const past = all.filter(([m]) => m < thisMonth).slice(-PAST_MONTHS);
    const future = all.filter(([m]) => m >= thisMonth);
    return [...past, ...future].map(([month, count]) => ({ month, count }));
  }, [byDay, thisMonth]);

  // Hide any chip the row cannot show WHOLE. Every chip stays in the DOM and in flow, so widths remain
  // measurable and the row can grow back when the pane widens — slicing the list instead would leave
  // nothing to measure and the strip could never recover.
  const glanceRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const strip = glanceRef.current;
    if (!strip) return undefined;
    const fit = () => {
      const chips = Array.from(strip.children) as HTMLElement[];
      for (const chip of chips) chip.style.visibility = '';
      for (const chip of chips) {
        if (chip.offsetLeft + chip.offsetWidth > strip.clientWidth) chip.style.visibility = 'hidden';
      }
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [glanceMonths]);

  /** Tooltip text for one day: its milestones, then its tasks by which date put them there. */
  const dayTip = (day: DayItems): string => [
    ...day.planned.map((m) => `${withProject(m.label, m.projectManagementProjectId)} — ${strings.Milestone()}`),
    ...day.done.map((m) => `${withProject(m.label, m.projectManagementProjectId)} — ${strings.MilestoneCompleted()}`),
    ...day.estimated.map((t) => `${withProject(t.label, t.projectManagementProjectId)} — ${strings.Estimated()}`),
    ...day.actual.map((t) => `${withProject(t.label, t.projectManagementProjectId)} — ${strings.Actual()}`),
  ].join('\n');

  if (!byDay.size) {
    return <ScheduleStyled><div className="empty">{strings.NoDatedItems()}</div></ScheduleStyled>;
  }

  return (
    <ScheduleStyled>
      <div className="glance" ref={glanceRef}>
        {
          glanceMonths.map(({ month, count }) => (
            <button
              className={`chip${month === monthStart ? ' selected' : ''}${month === thisMonth ? ' current' : ''}`}
              key={month}
              onClick={() => setMonthStart(month)}
              data-tooltip-id="pm-cal-tip"
              data-tooltip-content={strings.SelectThisMonth()}
            >
              {monthLabel(month, true)} <span className="count">· {count}</span>
            </button>
          ))
        }
      </div>

      <div className="cal-head">
        <button className="nav" onClick={() => setMonthStart(shiftMonth(monthStart, -1))} data-tooltip-id="pm-cal-tip" data-tooltip-content={strings.PreviousMonth()}>
          <Icon icon="chevronLeft" />
        </button>
        <span className="month">{monthLabel(monthStart)}</span>
        <button className="nav" onClick={() => setMonthStart(shiftMonth(monthStart, 1))} data-tooltip-id="pm-cal-tip" data-tooltip-content={strings.NextMonth()}>
          <Icon icon="chevronRight" />
        </button>
        <span className="spacer" />
        {monthStart !== thisMonth && <Button className="secondary compact" onClick={() => setMonthStart(thisMonth)}>{strings.Today()}</Button>}
      </div>

      <div className="dow">
        {Array.from({ length: 7 }, (_, i) => <span key={i}>{strings.DayOfWeekShort(i)}</span>)}
      </div>
      <div className="grid">
        {
          cells.map((c) => {
            const day = byDay.get(c.key) || emptyDay();
            const ms = [...day.planned, ...day.done];
            // Actuals first: only the first TASKS_PER_CELL survive, and a completed task is the more
            // informative of the two — sorting estimates ahead of it would hide every actual in a busy cell.
            const taskRows = [
              ...day.actual.map((t) => ({ t, actual: true })),
              ...day.estimated.map((t) => ({ t, actual: false })),
            ];
            const shown = taskRows.slice(0, TASKS_PER_CELL);
            const tip = dayTip(day);
            return (
              <div
                className={`cell${c.inMonth ? '' : ' out'}${ms.length ? ' ms' : ''}${c.key === todayKey ? ' today' : ''}`}
                key={c.key}
                data-tooltip-id={tip ? 'pm-cal-tip' : undefined}
                data-tooltip-content={tip || undefined}
              >
                <span className="num">{c.num}</span>
                {
                  ms.slice(0, MILESTONES_PER_CELL).map((m, i) => (
                    // Index-keyed: a milestone planned and completed on the same day appears twice, and
                    // both entries are the same object, so _id alone would collide.
                    <span className="item ms" key={`${m._id}-${i}`}>
                      {milestoneColor && <span className="ms-dot" style={{ background: milestoneColor(m) }} />}
                      <span className="txt">{withProject(m.label, m.projectManagementProjectId)}</span>
                    </span>
                  ))
                }
                {ms.length > MILESTONES_PER_CELL && <span className="more">{strings.MoreCount(ms.length - MILESTONES_PER_CELL)}</span>}
                {
                  shown.map((p) => (
                    <button
                      className={`item task${p.actual ? ' actual' : ''}${onOpenTask ? '' : ' static'}`}
                      key={`${p.t._id}-${p.actual ? 'a' : 'e'}`}
                      onClick={onOpenTask ? () => onOpenTask(p.t._id) : undefined}
                    >
                      <Icon icon="check" fill={p.actual ? css.primaryColor : css.grey4} />
                      <span className="txt">{withProject(p.t.label, p.t.projectManagementProjectId)}</span>
                    </button>
                  ))
                }
                {taskRows.length > shown.length && <span className="more">{strings.MoreCount(taskRows.length - shown.length)}</span>}
              </div>
            );
          })
        }
      </div>

      <div className="cal-foot">
        <span className="key"><Icon icon="check" fill={css.grey4} /> {strings.ScheduleLegendEstimated()}</span>
        <span className="key"><Icon icon="check" fill={css.primaryColor} /> {strings.ScheduleLegendActual()}</span>
        {!!milestones.length && <span className="key"><span className="swatch" /> {strings.ScheduleLegendMilestone()}</span>}
        {!!undated && <span className="undated">{strings.UndatedTasksCount(undated)}</span>}
      </div>

      {/* One tooltip for ~294 cells — per-cell <Tooltip> instances would be hundreds of portals a render. */}
      <Tooltip id="pm-cal-tip" style={{ whiteSpace: 'pre-line' }} />
    </ScheduleStyled>
  );
};
